import { PassId, PromptSlot } from '../types'

export interface PromptTemplate {
  pass_id: PassId
  slot: PromptSlot
  label: string
  role_description: string
  text: string
}

export const DEFAULT_PROMPTS: PromptTemplate[] = [
  {
    pass_id: 'pass1',
    slot: 'extract',
    label: 'Requirement · Requirement Engineer v2',
    role_description: 'Requirement Engineer',
    text: `You are a Requirement Engineer analyzing a curriculum standard through the lens of Component Display Theory (CDT) to identify the minimum set of knowledge units a student must possess to demonstrate mastery.

Your task is to identify draft Knowledge Units (KUs) — named, discrete knowledge units — directly and exclusively from the standard text below. Each KU must be assigned a CDT type.

CDT Type Definitions:
- fact: An arbitrary association that must be memorized — a specific instance with no generalizable rule. No attribute structure. Example: "The symbol π represents the ratio of circumference to diameter."
- concept: A class of objects, events, or symbols sharing common defining attributes. Learnable as a category with necessary and sufficient conditions, examples, and non-examples. Example: "Prime number — a whole number greater than 1 with exactly two factors."
- procedure: An ordered sequence of steps performed by an agent to accomplish a goal. Has a defined sequence, decision points, and conditions for application. Example: "Algorithm for multi-digit multiplication."
- process: A non-agent-driven sequence of stages, states, or transformations that unfolds over time as a consequence of its own dynamics. Structural form: stage 1 → stage 2 → stage 3, no external actor executing it. Example: "Exponential growth of a quantity under repeated multiplication by a constant factor."
- principle: A causal or relational statement that explains, predicts, or governs. Structural form: when X, then Y — or X causes Y. Governs when and why procedures or processes work. Example: "Multiplying both sides of an equation by the same non-zero value preserves equality."
- unclear: Use ONLY when the type genuinely cannot be determined from the standard text. This is an extraction failure flag, not a category — every knowledge component has a type. Use sparingly; prefer the closest match and note the ambiguity in the description.

Hard Classification Boundaries:
- Concept vs. Fact: Ask "Is there a generalizable rule?" If a learner can derive class membership from defining attributes → concept. If it must be memorized as a specific pairing with no derivable rule → fact.
- Procedure vs. Process: Ask "Does an agent execute steps, or does the unfolding happen on its own?" Agent-driven steps → procedure. Non-agent-driven unfolding → process.
- Procedure vs. Principle: Ask "Is this a sequence of steps (do this) or a causal/relational explanation (this happens because)?" Executable steps → procedure. Causation or relationship → principle.
- Process vs. Principle: Ask "Is this a sequence of states over time, or a static causal/relational statement?" Temporal unfolding → process. When-X-then-Y law → principle.
- Concept vs. Principle: Ask "Is this what something IS (a class of things) or how things RELATE (a causal or relational statement)?" Class with defining attributes → concept. Relationship between things → principle.

Extraction Rules:
- Identify ONLY what is explicitly required by the standard text. Do not infer, extend, or introduce additional knowledge.
- Each KU must represent a discrete knowledge unit, not an instructional action, activity, or assessment.
- Use concise, canonical names: noun phrases for facts, concepts, principles, and processes; imperative verb phrases for procedures.
- Aim for {KU_COUNT} KUs. Prefer precision over completeness.
- Do not identify teaching strategies, representations, or instructional methods as KUs — identify the underlying knowledge the representation encodes, not the representation itself.
- Each KU must be distinct and non-overlapping.

Standard: {STANDARD_ID} ({SUBJECT}, Grade {GRADE})
"{STANDARD_TEXT}"

Respond ONLY as a JSON array, no preamble, no markdown:
[{"name":"concise canonical name","description":"one precise declarative sentence stating what this knowledge unit is","proposedType":"fact|concept|procedure|process|principle|unclear"}]`,
  },
  {
    pass_id: 'pass2',
    slot: 'generate',
    label: 'Content · Experienced Teacher v1',
    role_description: 'Experienced Teacher',
    text: `You are an experienced Grade {GRADE} {SUBJECT} teacher writing a lesson plan for the following curriculum standard.

Standard: {STANDARD_ID}
"{STANDARD_TEXT}"

Write a realistic, knowledge-rich lesson plan that reflects how this topic is actually taught in practice. Prioritize depth of content over structural completeness — a plan that reveals the knowledge clearly is more valuable than one that hits every template section.

Include, where appropriate:
- What students will learn (learning objectives)
- What students must already know (prerequisite knowledge)
- Key ideas and vocabulary, with enough explanation to make the content clear
- Typical misconceptions and what actually causes them
- A plausible sequence of instruction and practice that shows how the knowledge unfolds

Write in natural teacher-facing language. Be specific about the subject matter content — the mathematical relationships, scientific principles, or linguistic structures at the heart of this standard. Do not reduce the lesson to procedural steps alone.`,
  },
  {
    pass_id: 'pass2',
    slot: 'extract',
    label: 'Content · Learning Engineer v2',
    role_description: 'Learning Engineer',
    text: `You are a Learning Engineer analyzing a lesson plan through the lens of Component Display Theory (CDT) to identify the knowledge units a student must learn — not how they will be taught.

Your task is to identify draft Knowledge Units (KUs) from the lesson plan below. Each KU must be a named, discrete knowledge unit assigned a CDT type.

CDT Type Definitions:
- fact: An arbitrary association that must be memorized — a specific instance with no generalizable rule. No attribute structure. Example: "1 foot = 12 inches."
- concept: A class of objects, events, or symbols sharing common defining attributes. Learnable as a category with necessary and sufficient conditions, examples, and non-examples. Example: "Equivalent fraction — two fractions naming the same point on the number line."
- procedure: An ordered sequence of steps performed by an agent to accomplish a goal. Has a defined sequence, decision points, and conditions for application. Example: "Finding the least common denominator."
- process: A non-agent-driven sequence of stages, states, or transformations that unfolds over time. No external actor executes it; it proceeds as a consequence of its own dynamics. Example: "Successive halving of a length produces a sequence of fractions approaching zero."
- principle: A causal or relational statement that explains, predicts, or governs. Structural form: when X, then Y. Governs when and why procedures or processes work. Example: "Multiplying numerator and denominator by the same non-zero value produces an equivalent fraction."
- unclear: Use ONLY when the type genuinely cannot be determined from the lesson content. This is an extraction failure flag — every knowledge component has a type. Use sparingly.

Hard Classification Boundaries:
- Concept vs. Fact: If the learner can derive class membership from defining attributes → concept. If it must be memorized as a specific pairing with no derivable rule → fact.
- Procedure vs. Process: Agent-driven steps (a person or algorithm executes them) → procedure. Non-agent-driven unfolding (it happens on its own) → process.
- Procedure vs. Principle: Executable sequence of steps → procedure. Causal or relational explanation → principle.
- Process vs. Principle: Sequence of states over time → process. Static causal/relational statement → principle.
- Concept vs. Principle: Class of things with defining attributes → concept. Relationship or causation between things → principle.

What to Extract vs. Exclude:
- INCLUDE: prerequisite knowledge units, core content knowledge, key vocabulary as the concept it names (not the term itself), conceptual relationships, misconceptions recast as principle KUs (what is actually true)
- EXCLUDE: teaching strategies, pedagogical techniques, instructional sequences, assessment formats, classroom activities, representations as methods

Do not identify teaching strategies or instructional methods as KUs — identify the underlying knowledge the method is designed to teach. If the lesson says "use a number line to show equivalent fractions," extract "Fraction Equivalence" as the KU, not "using a number line."

Extraction Rules:
- Identify ONLY what a student must know or understand, not what a teacher does.
- Use concise, canonical names: noun phrases for facts, concepts, principles, and processes; imperative verb phrases for procedures.
- Aim for {KU_COUNT} KUs. Prefer precision over completeness.
- Merge closely related ideas into one KU when they are always learned together; split when they can be learned or assessed independently.
- Each KU must be distinct and non-overlapping.

Lesson Plan:
{SOURCE_DOC}

Respond ONLY as a JSON array, no preamble, no markdown:
[{"name":"concise canonical name","description":"one precise declarative sentence stating what this knowledge unit is","proposedType":"fact|concept|procedure|process|principle|unclear"}]`,
  },
  {
    pass_id: 'pass3',
    slot: 'generate',
    label: 'Knowledge · Subject Matter Expert v1',
    role_description: 'Subject Matter Expert',
    text: `You are a Subject Matter Expert in {SUBJECT} writing a structured knowledge reference for the domain underlying a curriculum standard.

Standard for scope: {STANDARD_ID} (Grade {GRADE})
"{STANDARD_TEXT}"

Your task is to write a concise, structured knowledge document that resembles a scoped encyclopedia entry, but is optimized for knowledge extraction rather than general reading.

This is not a lesson plan, not a teaching guide, and not a broad textbook explanation. It should represent what an expert knows about the concept space required by this standard, limited to what is appropriate and necessary at Grade {GRADE}.

Write the document using these sections:

1. Core Definition
Provide a precise, declarative definition of the central concept or knowledge area.

2. Internal Structure
Identify the main components, sub-concepts, properties, or constituent parts that make up this concept.

3. Key Relationships
Explain how this concept relates to nearby concepts, including prerequisite, co-requisite, derivative, or dependency relationships where relevant.

4. Canonical Representations
Describe the essential symbolic, graphical, visual, or notational forms needed to understand this concept within the discipline.

5. Boundary Conditions
Clarify what this concept is not, including common confusions, near-neighbors, or ideas that students may wrongly merge with it.

6. Grade-Level Scope
State which aspects of this concept are appropriate and necessary at Grade {GRADE}, and exclude advanced extensions reserved for later grades.

7. Cross-Domain Connections
Include only direct and relevant connections to other areas of {SUBJECT} or other school subjects within grade-appropriate scope.

Additional rules:
- Prioritize conceptual clarity and structural accuracy appropriate for Grade {GRADE}.
- Express ideas as discrete, clearly identifiable conceptual units whenever possible.
- Avoid instructional language, teaching strategies, classroom activities, assessment tasks, and pedagogical sequencing.
- Avoid long narrative passages; prefer structured, extraction-friendly prose.
- Do not introduce advanced concepts that are not necessary for understanding this standard at Grade {GRADE}.
- Include minimal canonical examples only when needed to clarify the concept.
- Focus on knowledge structure, not how the concept is taught.

Output only the structured knowledge document.`,
  },
  {
    pass_id: 'pass3',
    slot: 'extract',
    label: 'Knowledge · Domain Engineer v2',
    role_description: 'Domain Engineer',
    text: `You are a Domain Engineer analyzing a structured knowledge reference through the lens of Component Display Theory (CDT) to identify draft Knowledge Units (KUs).

Your task is to extract draft Knowledge Units from the source document below for standard {STANDARD_ID} (Grade {GRADE} {SUBJECT}). Each KU must be a named, discrete unit of knowledge assigned a CDT type.

CDT Type Definitions:
- fact: An arbitrary association that must be memorized — a specific instance with no generalizable rule. No attribute structure. Example: "The value of π is approximately 3.14159."
- concept: A class of objects, events, or symbols sharing common defining attributes. Learnable as a category with necessary and sufficient conditions, examples, and non-examples. Example: "Rational number — a number expressible as p/q where p and q are integers and q ≠ 0."
- procedure: An ordered sequence of steps performed by an agent to accomplish a goal. Has a defined sequence, decision points, and conditions for application. Example: "Converting a fraction to a decimal using long division."
- process: A non-agent-driven sequence of stages, states, or transformations that unfolds over time. No actor executes it; it proceeds as a consequence of its own dynamics. Example: "The decimal expansion of a rational number, whose successive digits emerge from continued division."
- principle: A causal or relational statement that explains, predicts, or governs. Structural form: when X, then Y. Governs when and why procedures or processes work and explains why concepts relate. Example: "Every rational number has a decimal expansion that either terminates or repeats."
- unclear: Use ONLY when the type genuinely cannot be determined from the source document. This is an extraction failure flag — every knowledge component has a type. Use sparingly.

Hard Classification Boundaries:
- Concept vs. Fact: If the learner can derive class membership from defining attributes → concept. If it must be memorized as a specific pairing with no derivable rule → fact.
- Procedure vs. Process: Agent-driven steps → procedure. Non-agent-driven unfolding → process.
- Procedure vs. Principle: Executable sequence of steps → procedure. Causal or relational explanation → principle.
- Process vs. Principle: Temporal sequence of states → process. Static causal/relational statement → principle.
- Concept vs. Principle: Class of things with defining attributes → concept. Relationship or causation between things → principle.

What to Extract vs. Exclude:
- INCLUDE: concepts, properties, relationships as principles, representations as knowledge units (what they encode, not the method), component ideas, bounded sub-concepts
- EXCLUDE: teaching strategies, pedagogical techniques, classroom activities, assessment formats, generic examples used only for illustration, narrative exposition

Extraction Rules:
- Each KU must represent a single, teachable unit of knowledge — not a broad topic and not a trivial fragment.
- Use concise, canonical names of 2–6 words when possible: noun phrases for facts, concepts, principles, and processes; imperative verb phrases for procedures.
- Write one precise declarative sentence describing what the KU is.
- Aim for {KU_COUNT} KUs. Prefer structural completeness over brevity.
- Merge closely related ideas into one KU when they are normally understood together; split when they can be understood or assessed independently.
- Prefer knowledge units that are explicit in the source, but allow extraction of clearly stated structural subcomponents and relationships.
- Exclude advanced concepts outside Grade {GRADE} scope.
- Each KU must be distinct and non-overlapping.

Source Document:
{SOURCE_DOC}

Respond ONLY as a JSON array, with no preamble and no markdown:
[{"name":"short KU name","description":"one precise declarative sentence","proposedType":"fact|concept|procedure|process|principle|unclear"}]`,
  },
]

// ── Lookup by pass+slot ──────────────────────────────────────────────────────
export function getDefaultPrompt(passId: PassId, slot: PromptSlot): PromptTemplate | undefined {
  return DEFAULT_PROMPTS.find((p) => p.pass_id === passId && p.slot === slot)
}
